import { supabase } from '@/integrations/supabase/client';

export async function submitChangeRequest(
  productId: string,
  changeNotes: string,
  videoUrl?: string
) {
  // First, get AI summary
  const { data: summaryData, error: summaryError } = await supabase.functions.invoke(
    'summarize-change-request',
    {
      body: { changeNotes, videoUrl },
    }
  );

  if (summaryError) {
    console.error('Failed to get AI summary:', summaryError);
    throw summaryError;
  }

  // Get current user
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // Create change request
  const { data, error } = await supabase
    .from('vendor_change_requests')
    .insert({
      product_id: productId,
      vendor_id: user.id,
      change_notes: changeNotes,
      video_url: videoUrl,
      ai_summary: summaryData?.summary,
      ai_required_assets: summaryData?.required_assets,
      status: 'open',
    })
    .select()
    .single();

  if (error) throw error;
  
  // Notify admins via GHL
  await supabase.functions.invoke('notify-change-request-submitted', {
    body: { requestId: data.id }
  }).catch(err => {
    console.error('Failed to send admin notification:', err);
    // Don't fail the change request if notification fails
  });
  
  return data;
}

export async function getVendorChangeRequests(vendorId: string) {
  const { data, error } = await supabase
    .from('vendor_change_requests')
    .select('*, products(title, image)')
    .eq('vendor_id', vendorId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

export async function getAllChangeRequests() {
  const { data, error } = await supabase
    .from('vendor_change_requests')
    .select('*, products(title, image, vendor_name, vendor_email)')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

export async function updateChangeRequestStatus(
  requestId: string,
  status: 'open' | 'in_progress' | 'completed' | 'rejected',
  adminResponse?: string
) {
  const { data: { user } } = await supabase.auth.getUser();
  
  const updates: any = {
    status,
    admin_response: adminResponse,
  };

  if (status !== 'open') {
    updates.resolved_at = new Date().toISOString();
    updates.resolved_by = user?.id;
  }

  const { error } = await supabase
    .from('vendor_change_requests')
    .update(updates)
    .eq('id', requestId);

  if (error) throw error;
  
  // Notify vendor of decision via GHL
  if (status === 'completed' || status === 'rejected') {
    await supabase.functions.invoke('notify-change-request-decision', {
      body: { requestId, decision: status, adminResponse }
    }).catch(err => {
      console.error('Failed to send vendor decision notification:', err);
      // Don't fail the status update if notification fails
    });
  }
}
